import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TextField, MenuItem, Button, Card, CardContent, Grid, InputAdornment } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Iconify from '../../../components/iconify';
import { ESCALATION_SEVERITY, ESCALATION_SEVERITY_LABELS } from '../constants';

const EscalationFilters = ({ filters, onFilterChange, onResetFilters }) => {
  const [severityFilter, setSeverityFilter] = useState(filters.severity || '');
  const [startDateFilter, setStartDateFilter] = useState(filters.startDate ? dayjs(filters.startDate) : null);
  const [endDateFilter, setEndDateFilter] = useState(filters.endDate ? dayjs(filters.endDate) : null);
  const [searchTerm, setSearchTerm] = useState(filters.search || '');

  const handleSeverityChange = (event) => {
    const value = event.target.value;
    setSeverityFilter(value);
    onFilterChange('severity', value || undefined);
  };

  const handleStartDateChange = (date) => {
    setStartDateFilter(date);
    onFilterChange('startDate', date ? date.format('YYYY-MM-DD') : undefined);
  };

  const handleEndDateChange = (date) => {
    setEndDateFilter(date);
    onFilterChange('endDate', date ? date.format('YYYY-MM-DD') : undefined);
  };

  // Debounced search handler
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          onFilterChange('search', value || undefined);
        }, 300);
      };
    })(),
    [onFilterChange]
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleReset = () => {
    setSeverityFilter('');
    setStartDateFilter(null);
    setEndDateFilter(null);
    setSearchTerm('');
    onResetFilters();
  };

  // Sync local state when filters prop changes
  useEffect(() => {
    setSeverityFilter(filters.severity || '');
    setStartDateFilter(filters.startDate ? dayjs(filters.startDate) : null);
    setEndDateFilter(filters.endDate ? dayjs(filters.endDate) : null);
    setSearchTerm(filters.search || '');
  }, [filters]);

  const hasActiveFilters = severityFilter || startDateFilter || endDateFilter || searchTerm;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search by Project or Description"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search escalations..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Severity"
              value={severityFilter}
              onChange={handleSeverityChange}
              size="small"
            >
              <MenuItem value="">All Severities</MenuItem>
              {Object.values(ESCALATION_SEVERITY).map((severity) => (
                <MenuItem key={severity} value={severity}>
                  {ESCALATION_SEVERITY_LABELS[severity]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={startDateFilter}
                onChange={handleStartDateChange}
                inputFormat="DD-MMM-YYYY"
                maxDate={endDateFilter || dayjs()}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={endDateFilter}
                onChange={handleEndDateChange}
                inputFormat="DD-MMM-YYYY"
                minDate={startDateFilter || undefined}
                maxDate={dayjs()}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant={hasActiveFilters ? 'outlined' : 'text'}
              color="error"
              onClick={handleReset}
              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              size="small"
              disabled={!hasActiveFilters}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

EscalationFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
};

export default EscalationFilters;
